import React, { useState, useEffect } from 'react';
import Card from '@albertsons/uds/molecule/Card';
import './StoreOrderHistoryTab.css';
import DateSelector from '@albertsons/uds/molecule/DateSelector';
import Table from '@albertsons/uds/molecule/Table';
import { Column } from '@albertsons/uds/molecule/Table/Table.types';
import Search from '@albertsons/uds/molecule/Search';
import { StoreOrderHistoryDTO } from '../dto/StoreOrderHistoryDTO';
import getStoreOrderHistory from '../../../services/storeOrderHistorySevices/GetStoreOrderHistory';

function StoreOrderHistoryTab() {
  const [orderHistory, setOrderHistory] = useState<StoreOrderHistoryDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState<Date>();
  const [toDate, setToDate] = useState<Date>();
  const [isOpen, setOpen] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    getStoreOrderHistory(0, 1000, setIsLoading, setOrderHistory, () => {});
  }, []);

  const isDateRangeValid = (): boolean => {
    if (!fromDate || !toDate) return true;
    return fromDate <= toDate;
  };

  const filteredItems = orderHistory.filter((item) => {
    const matchesSearch = searchTerm.trim()
      ? item.lastUpdatedUserId?.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    const lastUpdated = item.lastUpdatedDate ? new Date(item.lastUpdatedDate) : null;

    const matchesDate =
      (!fromDate || (lastUpdated && lastUpdated >= fromDate)) &&
      (!toDate || (lastUpdated && lastUpdated <= toDate));

    return matchesSearch && matchesDate;
  });

  const totalPages = Math.ceil(filteredItems.length / pageSize);
  const startIndex = currentPage * pageSize;
  const paginatedItems = filteredItems.slice(startIndex, startIndex + pageSize);

  const headers: Column<StoreOrderHistoryDTO>[] = [
    { id: 'source', label: 'Source', value: 'source', sortable: false },
    { id: 'distributionCenterCd', label: 'Target Profile', value: 'distributionCenterCd', sortable: false },
    { id: 'maRegion', label: 'Target Region', value: 'maRegion', sortable: false },
    { id: 'lastUpdatedDate', label: 'Date of Load (UTC)', value: 'lastUpdatedDate', sortable: false },
    { id: 'lastUpdatedUserId', label: 'Last Updated By', value: 'lastUpdatedUserId', sortable: false }
  ];

  return (
    <Card
      collapsible
      title="Store Order History"
      isOpen={isOpen}
      onChange={setOpen}
      className="min-w-[600px] mt15"
    >
      <Card.Content>
        <div className="flex-container">
          <div>Load Date Range</div>
        </div>
        <div className="flex-container mt-1rem">
          <DateSelector value={fromDate} onChange={setFromDate} />
          <DateSelector value={toDate} onChange={setToDate} />
        </div>
        {!isDateRangeValid() && (
          <div style={{ color: 'red' }}>
            Invalid date range. Please select a valid range.
          </div>
        )}

        <div className="card" style={{ padding: '4%' }}>
          <h3 style={{ fontWeight: 'bold' }}>Search</h3>
          {React.createElement(Search as any, {
            size: "sm",
            value: searchTerm,
            onChange: (value: string) => {
              setSearchTerm(value);
              setCurrentPage(0);
            },
            placeholder: "Search by last updated user"
          })}

          {isLoading ? (
            <div style={{ textAlign: 'center', margin: '2rem 0' }}>Loading...</div>
          ) : filteredItems.length > 0 ? (
            <>
              <div
                className="bordered-table"
                style={{
                  overflowY: pageSize > 10 ? 'auto' : 'visible',
                  maxHeight: pageSize > 10 ? '420px' : 'none',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              >
                <Table
                  id="storeOrderHistoryTable"
                  itemKey="searchHistoryId"
                  items={paginatedItems}
                  columns={headers}
                  noPagination
                />
              </div>

              <div
                className="pagination-wrapper"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '1rem'
                }}
              >
                <div>
                  <label htmlFor="pageSize">Rows per page:&nbsp;</label>
                  <select
                    id="pageSize"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(parseInt(e.target.value));
                      setCurrentPage(0);
                    }}
                  >
                    {[10, 25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 0))}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </button>
                  <span>
                    Page {totalPages === 0 ? 0 : currentPage + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages - 1))}
                    disabled={currentPage >= totalPages - 1}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', margin: '2rem 0', color: '#888' }}>
              No results found.
            </div>
          )}
        </div>
      </Card.Content>
    </Card>
  );
}

export default StoreOrderHistoryTab;
